import { Deployment, foldDeployment_ } from "@/domain/deployment";
import { localesForDeployment, namedLocale, useI18n } from "@/domain/i18n";
import { MenuItem } from "@/domain/menu";
import useWindowScrollPosition from "@rehooks/window-scroll-position";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import Tether from "tether";
import LogoCH from "../public/images/gemeinwohl_logo_ch.svg";
import LogoDE from "../public/images/gemeinwohl_logo_de.svg";

interface HeaderProps {
  menuItems: Array<MenuItem>;
  deployment: Deployment;
}

export default function AutoToggleHeader({ menuItems, deployment }: HeaderProps) {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [isSeeThrough, setIsSeeThrough] = React.useState(true);
  const pos = useWindowScrollPosition({ throttle: 100 });

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      setIsSeeThrough(pos.y <= window.innerHeight - 40);
    }
  }, [pos.y, isSeeThrough]);

  return React.useMemo(() => {
    return (
      <header className="landing-page__site-header">
        <div
          ref={headerRef}
          className={`site-header site-header--large ${isSeeThrough ? "site-header--see-through" : ""}`}
          style={isSeeThrough ? {} : { backgroundColor: "white", zIndex: 1000 }}
        >
          <div className="site-header__content">
            <Header menuItems={menuItems} deployment={deployment} />
          </div>
        </div>
      </header>
    );
  }, [isSeeThrough, menuItems, deployment]);
}

export const Header = ({ menuItems, deployment }: HeaderProps) => {
  const I18n = useI18n();
  return (
    <>
      <Link href="/">
        <a className="site-header__logo site-logo">
          <h1 className="site-logo__title-fallback">{I18n.t("meta.title")}</h1>
          {foldDeployment_(deployment, {
            ch: () => <LogoCH />,
            de: () => <LogoDE />,
          })}
        </a>
      </Link>
      <SiteMenu menuItems={menuItems} deployment={deployment} />
    </>
  );
};

interface SiteMenuProps {
  menuItems: Array<MenuItem>;
  deployment: Deployment;
}

function SiteMenu({ menuItems, deployment }: SiteMenuProps) {
  const router = useRouter();

  const toggleRef = React.useRef<HTMLUListElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    let tether: Tether | undefined;
    if (toggleRef.current !== null && dropdownRef.current !== null) {
      tether = new Tether({
        target: toggleRef.current,
        element: dropdownRef.current,
        attachment: "top center",
        targetAttachment: "bottom center",
        enabled: false,
      });

      let siteMenuToggleHover = false;
      let siteMenuDropdownHover = false;

      const updateVisibility = () => {
        if (siteMenuToggleHover || siteMenuDropdownHover) {
          if (tether && dropdownRef.current) {
            dropdownRef.current.style.display = "block";
            tether.enable();
            tether.position();
          }
        } else {
          if (tether && dropdownRef.current) {
            dropdownRef.current.style.display = "none";
            tether.disable();
          }
        }
      };

      toggleRef.current.addEventListener("mouseover", () => {
        siteMenuToggleHover = true;
        updateVisibility();
      });
      toggleRef.current.addEventListener("mouseout", () => {
        siteMenuToggleHover = false;
        updateVisibility();
      });

      dropdownRef.current.addEventListener("mouseover", () => {
        siteMenuDropdownHover = true;
        updateVisibility();
      });
      dropdownRef.current.addEventListener("mouseout", () => {
        siteMenuDropdownHover = false;
        updateVisibility();
      });
    }

    () => {
      if (tether) tether.destroy();
    };
  }, []);

  // Remove locale from path for matching routes, i.e. /de/hello -> /hello (but / -> /)
  const currentPath = router.asPath.match(/^\/(\w\w)\//) ? router.asPath.slice(3) : router.asPath;

  return (
    <>
      <nav className="site-header__nav site-menu">
        <ul>
          {menuItems.map((item, i) => (
            <li
              key={`menu-item-${item.path}`}
              className={[
                i === 0 && "first",
                i === menuItems.length - 1 && "last",
                (item.menu_match && new RegExp(item.menu_match).test(currentPath)) || item.path === currentPath
                  ? "is-active"
                  : false,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <Link href={item.path}>
                <a>{item.title}</a>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      <div className="site-header__language site-menu">
        <ul ref={toggleRef} id="site-menu-toggle" className="site-menu-toggle">
          <li>
            <a>
              {namedLocale(router.locale as string)}{" "}
              <svg width="6" height="10" viewBox="0 0 6 4">
                <path d="M3 4L0 0h6" fill="currentColor" fillRule="evenodd" />
              </svg>
            </a>
          </li>
        </ul>

        <div ref={dropdownRef} id="site-menu-dropdown" className="site-menu-language-popup" style={{ display: "none" }}>
          <div className="site-menu-language-popup-inner">
            {localesForDeployment(deployment).map((locale) => (
              <div key={locale} className={router.locale === locale ? "is-active" : ""}>
                <Link href={{ pathname: router.pathname, query: router.query }} locale={locale}>
                  <a>{namedLocale(locale)}</a>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
